import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';
import { 
  useCourseApi, 
  CourseDetails
} from '../hooks/useCourseApi';
import UnitAccessTimer from '../components/common/UnitAccessTimer';

// Helper function to check if a unit is accessible based on enrollment date
const isUnitAccessible = (unitIndex: number, enrollmentDate: string, isOpenToAll: boolean): { accessible: boolean; message?: string; daysLeft?: number } => {
  if (isOpenToAll) {
    return { accessible: true };
  }

  if (!enrollmentDate) {
    return { accessible: false, message: 'تاريخ التسجيل غير متوفر' };
  }

  const enrollment = new Date(enrollmentDate);
  const now = new Date();
  const daysSinceEnrollment = Math.floor((now.getTime() - enrollment.getTime()) / (1000 * 60 * 60 * 24));
  
  const unitStartDay = 0;
  const unitEndDay = (unitIndex + 1) * 7;
  
  if (daysSinceEnrollment >= unitStartDay && daysSinceEnrollment < unitEndDay) {
    const daysLeft = unitEndDay - daysSinceEnrollment;
    return { accessible: true, daysLeft };
  }
  
  if (daysSinceEnrollment < unitStartDay) {
    const daysUntilStart = unitStartDay - daysSinceEnrollment;
    return { 
      accessible: false, 
      message: `ستصبح هذه الوحدة متاحة خلال ${daysUntilStart} ${daysUntilStart === 1 ? 'يوم' : 'أيام'}` 
    };
  }
  
  return { 
    accessible: false, 
    message: 'انتهت فترة الوصول لهذه الوحدة' 
  };
};

// Function to check if a lesson should be locked due to unsubmitted quiz
const isLessonLockedByQuiz = (unitIndex: number, lessonIndex: number, courseData: CourseDetails | null): boolean => {
  if (!courseData) return false;
  
  const unit = courseData.units[unitIndex];
  if (!unit) return false;
  
  // Check if any previous lesson in this unit is a quiz that hasn't been submitted
  for (let i = 0; i < lessonIndex; i++) {
    const lesson = unit.lessons[i];
    if (lesson.type === 'Quiz' && !lesson.isQuizSubmitted) {
      return true;
    }
  }
  
  // Check if any previous unit has an unsubmitted quiz
  for (let prevUnitIndex = 0; prevUnitIndex < unitIndex; prevUnitIndex++) {
    const prevUnit = courseData.units[prevUnitIndex];
    for (const lesson of prevUnit.lessons) {
      if (lesson.type === 'Quiz' && !lesson.isQuizSubmitted) {
        return true;
      }
    }
  }
  
  return false;
};

// Function to check if a unit should be locked due to unsubmitted quiz
const isUnitLockedByQuiz = (unitIndex: number, courseData: CourseDetails | null): boolean => {
  if (!courseData) return false;
  
  for (let prevUnitIndex = 0; prevUnitIndex < unitIndex; prevUnitIndex++) {
    const prevUnit = courseData.units[prevUnitIndex];
    for (const lesson of prevUnit.lessons) {
      if (lesson.type === 'Quiz' && !lesson.isQuizSubmitted) {
        return true;
      }
    }
  }
  
  return false;
};

const getUnitAccessStatus = (unitIndex: number, enrollmentDate: string, isOpenToAll: boolean, courseData: CourseDetails | null) => {
  const status = isUnitAccessible(unitIndex, enrollmentDate, isOpenToAll);
  const quizLocked = isUnitLockedByQuiz(unitIndex, courseData);
  
  if (quizLocked) {
    return {
      accessible: false,
      statusText: 'Complete Previous Quiz',
      statusColor: 'text-orange-600'
    };
  }
  
  if (status.accessible && status.daysLeft !== undefined) {
    return {
      ...status,
      statusText: `متاحة (${status.daysLeft} ${status.daysLeft === 1 ? 'يوم متبقي' : 'أيام متبقية'})`,
      statusColor: status.daysLeft <= 2 ? 'text-orange-600' : 'text-green-600'
    };
  }
  
  if (status.accessible && isOpenToAll) {
    return {
      ...status,
      statusText: 'متاحة دائماً',
      statusColor: 'text-green-600'
    };
  }
  
  return {
    ...status,
    statusText: status.message || 'غير متاحة',
    statusColor: 'text-red-600'
  };
};

const CourseCurriculum: React.FC = () => {
  useScrollToTop();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchCourseDetails, isLoading } = useCourseApi();
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [collapsedUnits, setCollapsedUnits] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [lastNavigationPath, setLastNavigationPath] = useState<string>('');
  const [lastBackRefresh, setLastBackRefresh] = useState<number>(0);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load course details function
  const loadCourseDetails = useCallback(async (forceRefresh = false, isInitial = false) => {
    if (!courseId || refreshing) {
      return;
    }

    try {
      if (!isInitial) {
        setRefreshing(true);
      }
      setError(null);
      console.log('Loading course details for ID:', courseId);
      
      const response = await fetchCourseDetails(courseId, forceRefresh);

      if (response?.success) {
        setCourseDetails(response.data);
        if (forceRefresh && !isInitial) {
          setRefreshSuccess(true);
          setTimeout(() => setRefreshSuccess(false), 2000);
        }
      } else {
        setError('فشل في تحميل تفاصيل الدورة');
      }
    } catch (err) {
      console.error('Error loading course:', err);
      setError('حدث خطأ أثناء تحميل الدورة');
    } finally {
      if (!isInitial) {
        setRefreshing(false);
      }
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  }, [courseId, fetchCourseDetails, refreshing]);

  // Load on mount and courseId changes with initial loading state
  useEffect(() => {
    if (courseId) {
      console.log('Loading course on mount/courseId change');
      
      // Clear browser cache for this page
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            if (name.includes('course') || name.includes('api')) {
              caches.delete(name);
              console.log(`Cleared cache: ${name}`);
            }
          });
        });
      }
      
      // Clear session storage related to this course
      const courseKeys = Object.keys(sessionStorage).filter(key => 
        key.includes(courseId || '') || key.includes('course')
      );
      courseKeys.forEach(key => {
        sessionStorage.removeItem(key);
        console.log(`Cleared session storage: ${key}`);
      });
      
      setInitialLoading(true);
      loadCourseDetails(true, true); // Force refresh on mount to prevent caching
    }
  }, [courseId]);

  // Refresh when navigating to curriculum page from anywhere (prevent duplicate calls)
  useEffect(() => {
    if (location.pathname.includes('/curriculum') && courseId && location.pathname !== lastNavigationPath) {
      console.log('Navigated to curriculum page from:', lastNavigationPath, 'to:', location.pathname);
      setLastNavigationPath(location.pathname);
      
      // Small delay to prevent rapid successive calls
      const timeoutId = setTimeout(() => {
        // Clear any cached data before refreshing
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
        loadCourseDetails(true, false); // Force refresh to get latest completion status, not initial
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, courseId, loadCourseDetails, lastNavigationPath]);

  // Handle browser back/forward navigation to curriculum page (with throttling)
  useEffect(() => {
    const throttledBackRefresh = () => {
      const now = Date.now();
      if (now - lastBackRefresh > 1000) { // Minimum 1 second between back refreshes
        setLastBackRefresh(now);
        
        // Clear browser cache before refreshing
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
        
        // Clear session storage
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('course') || key.includes(courseId || '')) {
            sessionStorage.removeItem(key);
          }
        });
        
        loadCourseDetails(true, false); // Not initial load
      }
    };

    const handlePopState = () => {
      if (location.pathname.includes('/curriculum') && courseId) {
        console.log('Browser back/forward to curriculum - refreshing data');
        throttledBackRefresh();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // This fires when page is restored from cache (browser back)
      if (event.persisted && location.pathname.includes('/curriculum') && courseId) {
        console.log('Page restored from cache - refreshing curriculum data');
        throttledBackRefresh();
      }
    };

    const handleFocus = () => {
      // Fires when user returns to this window/tab from video lesson
      if (location.pathname.includes('/curriculum') && courseId && !document.hidden) {
        console.log('Window focus on curriculum - refreshing data');
        throttledBackRefresh();
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.pathname, courseId, loadCourseDetails, lastBackRefresh]);

  // Manual refresh from storage events (lesson completion)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'course_data_updated' && event.newValue) {
        console.log('Manual refresh from lesson completion');
        loadCourseDetails(true, false); // Not initial load
        localStorage.removeItem('course_data_updated');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadCourseDetails]);


  const toggleUnit = (unitId: string) => {
    setCollapsedUnits(prev => 
      prev.includes(unitId) 
        ? prev.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const handleLessonClick = (unitIndex: number, lessonIndex: number) => {
    if (!courseDetails) {
      console.error('No course details available');
      return;
    }

    // Check if lesson is locked by quiz
    if (isLessonLockedByQuiz(unitIndex, lessonIndex, courseDetails)) {
      setError('يجب إكمال الاختبار السابق أولاً');
      return;
    }

    // Check if unit is accessible
    const unitAccess = isUnitAccessible(unitIndex, courseDetails.enrollmentDate, courseDetails.isOpenToAll);
    if (!unitAccess.accessible) {
      setError(unitAccess.message || 'هذه الوحدة غير متاحة حالياً');
      return;
    }

    const unit = courseDetails.units[unitIndex];
    if (!unit || !unit.lessons || lessonIndex >= unit.lessons.length) {
      console.error('Invalid unit or lesson index:', { unitIndex, lessonIndex, unit });
      setError('الدرس غير متوفر');
      return;
    }

    const lesson = unit.lessons[lessonIndex];
    console.log('Selected lesson:', lesson);
    
    // Check if quiz is already submitted
    if (lesson.type === 'Quiz' && lesson.isQuizSubmitted) {
      setError('تم إرسال هذا الاختبار مسبقاً ولا يمكن إعادة فتحه');
      return;
    }
    
    setError(null);
    
    // Navigate to appropriate player page
    if (lesson.type === 'Video') {
      navigate(`/course/${courseId}/lesson/${lesson.id}/video`);
    } else if (lesson.type === 'Quiz') {
      navigate(`/course/${courseId}/lesson/${lesson.id}/quiz`);
    }
  };

  const calculateProgress = () => {
    if (!courseDetails || !courseDetails.units) return 0;
    
    let totalLessons = 0;
    let completedLessons = 0;
    
    courseDetails.units.forEach(unit => {
      unit.lessons.forEach(lesson => {
        totalLessons++;
        if ((lesson.type === 'Video' && lesson.isCompleted) || 
            (lesson.type === 'Quiz' && lesson.isQuizSubmitted)) {
          completedLessons++;
        }
      });
    });
    
    return totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  };

  // Show initial loading screen when page is first opened
  if (initialLoading || (isLoading && !courseDetails)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <div className="relative">
            {/* Outer ring */}
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-primary mx-auto mb-6"></div>
            {/* Inner ring */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-indigo-400"></div>
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">جاري تحميل المنهج</h3>
          <p className="text-slate-600">يرجى الانتظار بينما نقوم بتحميل محتوى الدورة...</p>
          
          {/* Loading animation bars */}
          <div className="flex justify-center mt-6 space-x-1">
            <div className="w-2 h-8 bg-primary rounded animate-pulse" style={{animationDelay: '0ms'}}></div>
            <div className="w-2 h-6 bg-indigo-400 rounded animate-pulse" style={{animationDelay: '150ms'}}></div>
            <div className="w-2 h-8 bg-primary rounded animate-pulse" style={{animationDelay: '300ms'}}></div>
            <div className="w-2 h-6 bg-indigo-400 rounded animate-pulse" style={{animationDelay: '450ms'}}></div>
            <div className="w-2 h-8 bg-primary rounded animate-pulse" style={{animationDelay: '600ms'}}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !courseDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Course Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The course you\'re looking for doesn\'t seem to exist.'}</p>
          <button 
            onClick={() => navigate('/enrolled-courses')}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-200"
          >
            Back to My Courses
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation Bar */}
      <div className="bg-white shadow-sm border-b border-slate-200 fixed top-0 left-0 right-0 z-50">
        <div className="h-16 flex items-center justify-between px-4 lg:px-6 max-w-[1920px] mx-auto">
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <button 
              onClick={() => navigate('/enrolled-courses')}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors flex items-center gap-2 text-slate-700"
              aria-label="Back to Courses"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden sm:block">Back to Courses</span>
            </button>

            {/* Refresh Button */}
             <button 
               onClick={() => loadCourseDetails(true, false)}
               disabled={refreshing}
               className="p-2 rounded-xl hover:bg-slate-100 transition-colors flex items-center gap-2 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
               aria-label="Refresh Course Data"
             >
              <svg 
                className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="hidden sm:block">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>

          {/* Course Title and Progress */}
          <div className="flex-1 mx-6">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-slate-900 truncate">
                {courseDetails.courseName}
              </h1>
              {refreshing && (
                <div className="flex items-center gap-1 text-blue-600 text-sm">
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Updating...</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {`${totalLessons} Lessons`}
              </span>
              <div className="h-4 w-px bg-slate-200"></div>
              <span className="text-blue-600 font-medium">{calculateProgress().toFixed(0)}% Complete</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100">
            <div 
              className="h-full bg-blue-500 transition-all duration-300 ease-in-out"
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-16 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Course Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-slate-200">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                    d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{courseDetails.courseName}</h2>
                <p className="text-slate-600 mb-4">
                  استكشف محتوى الدورة واختر الدرس الذي تريد دراسته
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    {`${courseDetails.units.length} Units`}
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {`${totalLessons} Lessons`}
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {`${calculateProgress().toFixed(0)}% Complete`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Unit Access Timer - Only show if course is not open to all */}
          {courseDetails && !courseDetails.isOpenToAll && (
            <div className="mb-8">
              {courseDetails.units.map((unit, unitIndex) => {
                const unitAccess = getUnitAccessStatus(unitIndex, courseDetails.enrollmentDate, courseDetails.isOpenToAll, courseDetails);
                if (unitAccess.accessible && unitAccess.daysLeft) {
                  return (
                    <UnitAccessTimer 
                      key={unit.id}
                      unitIndex={unitIndex}
                      enrollmentDate={courseDetails.enrollmentDate}
                      totalUnits={courseDetails.units.length}
                      className="mb-4"
                    />
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* Success Message */}
          {refreshSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center animate-fade-in">
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Course data updated successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Course Units List */}
          <div className="space-y-6">
            {courseDetails.units.map((unit, unitIndex) => {
              const unitAccess = getUnitAccessStatus(unitIndex, courseDetails.enrollmentDate, courseDetails.isOpenToAll, courseDetails);
              const isUnitLocked = !unitAccess.accessible;
              
              return (
                <div key={unit.id} className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                  <button
                    className={`w-full p-6 flex items-center gap-4 transition-colors ${
                      isUnitLocked 
                        ? 'opacity-60 cursor-not-allowed bg-gray-50' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => !isUnitLocked && toggleUnit(unit.id.toString())}
                    disabled={isUnitLocked}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isUnitLocked 
                        ? 'bg-gray-200' 
                        : 'bg-gradient-to-br from-blue-500/10 to-blue-600/20'
                    }`}>
                      {isUnitLocked ? (
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      ) : (
                        <span className="text-blue-700 text-lg font-bold">{unitIndex + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className={`text-xl font-semibold mb-2 ${isUnitLocked ? 'text-gray-500' : 'text-slate-900'}`}>
                        {unit.unitName}
                      </h3>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm ${isUnitLocked ? 'text-gray-400' : 'text-slate-500'}`}>
                          {`${unit.lessons.length} Lessons`}
                        </p>
                        <span className={`text-sm font-medium ${unitAccess.statusColor}`}>
                          {unitAccess.statusText}
                        </span>
                      </div>
                    </div>
                    {!isUnitLocked && (
                      <svg 
                        className={`w-6 h-6 text-slate-400 transition-transform ${collapsedUnits.includes(unit.id.toString()) ? '' : 'rotate-180'}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>
                  
                  {!isUnitLocked && (
                    <div className={`${collapsedUnits.includes(unit.id.toString()) ? 'hidden' : ''}`}>
                      <div className="border-t border-slate-200">
                        {unit.lessons.map((lesson, lessonIndex) => {
                          const isLessonLocked = isLessonLockedByQuiz(unitIndex, lessonIndex, courseDetails);
                          const isCompleted = (lesson.type === 'Video' && lesson.isCompleted) || 
                                            (lesson.type === 'Quiz' && lesson.isQuizSubmitted);
                          const isQuizSubmitted = lesson.type === 'Quiz' && lesson.isQuizSubmitted === true;
                          const isDisabled = isLessonLocked || isQuizSubmitted;
                          
                          return (
                            <button
                              key={lesson.id}
                              className={`w-full p-4 flex items-center gap-4 transition-colors border-b border-slate-100 last:border-b-0 ${
                                isDisabled 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : 'hover:bg-slate-50 cursor-pointer'
                              }`}
                              onClick={() => !isDisabled && handleLessonClick(unitIndex, lessonIndex)}
                              disabled={isDisabled}
                            >
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isCompleted 
                                  ? 'bg-green-100' 
                                  : isQuizSubmitted
                                  ? 'bg-blue-100'
                                  : 'bg-slate-100'
                              }`}>
                                {isCompleted ? (
                                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : isQuizSubmitted ? (
                                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                ) : lesson.type === 'Video' ? (
                                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 text-left">
                                <h4 className="font-medium text-slate-900 mb-1">{lesson.lessonName}</h4>
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    {lesson.type === 'Video' ? (
                                      <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Video Lesson
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Quiz
                                      </>
                                    )}
                                  </span>
                                  {isCompleted && (
                                    <>
                                      <span className="text-slate-300">•</span>
                                      <span className="text-green-600 font-medium">Completed</span>
                                    </>
                                  )}
                                  {isQuizSubmitted && (
                                    <>
                                      <span className="text-slate-300">•</span>
                                      <span className="text-blue-600 font-medium">Submitted</span>
                                    </>
                                  )}
                                  {isLessonLocked && !isQuizSubmitted && (
                                    <>
                                      <span className="text-slate-300">•</span>
                                      <span className="text-orange-600 font-medium">Locked</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              {isQuizSubmitted ? (
                                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCurriculum;
